// src/components/VideoList.jsx
import React, { useEffect, useState, useContext } from 'react'
import axios from 'axios'
import { UserContext } from '../context'
import VideoCard from './VideoCard'
import PageIndex from './PageIndex'
import CategoryManager from './CategoryManager'
import { useCategories } from '../hooks/useCategories'

const FiltersBar = ({
  searchQuery, setSearchQuery,
  categories, selectedCategory, setSelectedCategory,
  onSearch
}) => {
  return (
    <div className="library__filters">
      {/* search */}
      <div className="input-wrap">
        <span className="input-icon">🔎</span>
        <input
          className="input"
          type="text"
          placeholder="Buscar cursos, instructores…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && onSearch()}
        />
      </div>

      {/* category */}
      <div className="select-wrap">
        <select
          className="select"
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
        >
          <option value="">Todas</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>{c.title}</option>
          ))}
        </select>
      </div>

      <button className="btn btn--primary" onClick={onSearch}>Buscar</button>
    </div>
  )
}

const VideoList = () => {
  const [state] = useContext(UserContext)

  // filters
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')

  // data
  const { categories, setCategories, reload: reloadCategories } = useCategories()
  const [videos, setVideos] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  // pagination
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const PAGE_SIZE = 9

  useEffect(() => { console.log('VideoList v2 mounted') }, [])

  const buildUrl = () => {
    const base = `${process.env.REACT_APP_BASE_URL}/api/video_list/`
    const params = new URLSearchParams()
    params.set('page', String(page))
    params.set('page_size', String(PAGE_SIZE))
    if (searchQuery) params.set('search', searchQuery)
    if (selectedCategory) params.set('category', selectedCategory)
    return `${base}?${params.toString()}`
  }

  const getVideos = async () => {
    setIsLoading(true)
    try {
      const res = await axios.get(buildUrl(), { timeout: 7000 })
      const total = res.data?.total_count ?? 0
      setTotalPages(Math.ceil(total / PAGE_SIZE))
      setVideos(res.data?.results || [])
    } catch (e) {
      console.error('Error getting videos', e)
      setVideos([])
      setTotalPages(0)
    } finally {
      setIsLoading(false)
    }
  }

  // Refetch whenever page, category, OR search query changes
  useEffect(() => {
    getVideos()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, selectedCategory, searchQuery])

  // Clicking “Buscar” resets to page 1; the effect above will refetch with the new searchQuery
  const triggerSearch = () => setPage(1)

  // category admin modal
  const [showCatMgr, setShowCatMgr] = useState(false)

  return (
    <div className="library container">
      <header className="library__header">
        <h1>Biblioteca de Cursos</h1>
        <p>Explora nuestra colección de clases de yoga y meditación</p>
      </header>

      <FiltersBar
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        categories={categories}
        selectedCategory={selectedCategory}
        setSelectedCategory={(v) => { setSelectedCategory(v); setPage(1) }}
        onSearch={triggerSearch}
      />

      {state.user?.is_staff && (
        <div style={{ marginBottom: '1rem' }}>
          <button className="btn btn--ghost" onClick={() => setShowCatMgr(true)}>
            Gestionar categorías
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="library__empty"><p>Cargando…</p></div>
      ) : videos.length > 0 ? (
        <>
          <div className="videos-grid">
            {videos.map((video) => (
              <VideoCard
                key={video.id}
                {...video}
                is_staff={state.user ? state.user.is_staff : false}
              />
            ))}
          </div>
          <PageIndex totalPages={totalPages} page={page} setPage={setPage} />
        </>
      ) : (
        <div className="library__empty"><p>No hay videos por aquí…</p></div>
      )}

      {showCatMgr && (
        <CategoryManager
          onClose={() => setShowCatMgr(false)}
          categories={categories}
          setCategories={setCategories}
          reload={reloadCategories}
        />
      )}
    </div>
  )
}

export default VideoList
